import React, { useState, useEffect } from "react";
import { Modal } from "react-bootstrap";
import { closeIcon } from "../../../../assets/svg";
import "./style.css";
import makeRequest from "../../../../api";
import { nutritionApiService } from "../../../../api/services";
import { toast } from "react-toastify";

interface mealPlanModal {
  open: boolean;
  setOpen: (target: boolean) => void;
  mealType: any;
  date: any;
  mealPlan: any;
  updateMealPlan: any;
  editId: any;
  editDish: any;
}

const EditDishModal = ({
  open,
  setOpen,
  mealType,
  date,
  mealPlan,
  updateMealPlan,
  editId,
  editDish,
}: mealPlanModal) => {
  const [servingCount, setServingCount] = useState(0);
  const [protein, setProtein] = useState(0);
  const [calorie, setCalorie] = useState(0);
  const [fat, setFat] = useState(0);
  const [fattyAcidTotalSaturated, setFattyAcidTotalSaturated] = useState(0);
  const [fibre, setFibre] = useState(0);
  const [sodium, setSodium] = useState(0);
  const [carbs, setCarbs] = useState(0);
  const [totalQuantity, setTotalQuantity] = useState(0);
  const [defaultValues, setDefaultValues] = useState({
    calorie: 0,
    protein: 0,
    carbs: 0,
    fat: 0,
    fattyAcidTotalSaturated: 0,
    fibre: 0,
    sodium: 0,
    servingQuantity: 0,
  });
  const [servingSizeOptions, setServiceSizeOptions] = useState([]);
  const [servingSize, setServingSize] = useState("");
  const [sizeQuantityMap, setSizeQuantityMap] = useState<{
    [key: string]: any;
  }>({});
  const preCalculatedValues = editDish?.recipe?.preCalculated;

  useEffect(() => {
    const quantityMap: { [key: string]: any } = {};
    servingSizeOptions?.forEach((item: any) => {
      const size: string = item?.size;
      quantityMap[size] = item?.serving_qty;
    });
    setSizeQuantityMap({ ...quantityMap });
  }, [servingSizeOptions]);

  useEffect(() => {
    if (editDish) {
      setServingCount(+editDish?.unitCount);
      setServingSize(editDish?.unitSize);
      setCalorie(+editDish?.recipe?.macro?.calorie);
      setProtein(+editDish?.recipe?.macro?.protein);
      setFat(+editDish?.recipe?.macro?.fat);
      setFattyAcidTotalSaturated(
        +editDish?.recipe?.micro?.fatty_acids_total_saturated
      );
      setFibre(+editDish?.recipe?.micro?.fibre);
      setSodium(+editDish?.recipe?.micro?.sodium);
      setCarbs(+editDish?.recipe?.macro?.carbs);
      setTotalQuantity(+editDish?.totalQuantity);
      setDefaultValues((d) => ({
        ...d,
        calorie: +editDish?.recipe?.macro?.calorie,
        protein: +editDish?.recipe?.macro?.protein,
        carbs: +editDish?.recipe?.macro?.carbs,
        fat: +editDish?.recipe?.macro?.fat,
        fattyAcidTotalSaturated:
          +editDish?.recipe?.micro?.fatty_acids_total_saturated,
        fibre: +editDish?.recipe?.micro?.fibre,
        sodium: +editDish?.recipe?.micro?.sodium,
        servingQuantity: +editDish?.totalQuantity,
      }));
      fetchServingSizeOptions(editDish);
    }
  }, [editDish]);

  useEffect(() => {
    if (servingSize === "" || sizeQuantityMap[servingSize] === 0) {
      setTotalQuantity(() => +servingCount * +defaultValues?.servingQuantity);
      let c = +defaultValues?.calorie * +servingCount;
      c = +c.toFixed(3);
      setCalorie(() => c);
      let p = +defaultValues?.protein * +servingCount;
      p = +p.toFixed(3);
      setProtein(() => p);
      let carbs = +defaultValues?.carbs * +servingCount;
      carbs = +carbs.toFixed(3);
      setCarbs(() => carbs);
      let fat = +defaultValues?.fat * +servingCount;
      fat = +fat.toFixed(3);
      setFat(() => fat);
      let fattyAcidTotalSaturated =
        +defaultValues?.fattyAcidTotalSaturated * +servingCount;
      fattyAcidTotalSaturated = +fattyAcidTotalSaturated.toFixed(3);
      setFattyAcidTotalSaturated(() => fattyAcidTotalSaturated);
      let fibre = +defaultValues?.fibre * +servingCount;
      fibre = +fibre.toFixed(3);
      setFibre(() => fibre);
      let sodium = +defaultValues?.sodium * +servingCount;
      sodium = +sodium.toFixed(3);
      setSodium(() => sodium);
    } else {
      setTotalQuantity(
        () =>
          +servingCount *
          +preCalculatedValues[servingSize]?.standard_values_in_grams
      );
      let c = +servingCount * +preCalculatedValues[servingSize]?.calorie;
      c = +c.toFixed(3);
      setCalorie(() => c);
      let p = +servingCount * +preCalculatedValues[servingSize]?.protein;
      p = +p.toFixed(3);
      setProtein(() => p);
      let carbs = +servingCount * +preCalculatedValues[servingSize]?.carbs;
      carbs = +carbs.toFixed(3);
      setCarbs(() => carbs);
      let fat = +servingCount * +preCalculatedValues[servingSize]?.fat;
      fat = +fat.toFixed(3);
      setFat(() => fat);
      let fattyAcidTotalSaturated =
        +servingCount *
        +preCalculatedValues[servingSize]?.fatty_acids_total_saturated;
      fattyAcidTotalSaturated = +fattyAcidTotalSaturated.toFixed(3);
      setFattyAcidTotalSaturated(() => fattyAcidTotalSaturated);
      let fibre = +servingCount * +preCalculatedValues[servingSize]?.fibre;
      fibre = +fibre.toFixed(3);
      setFibre(() => fibre);
      let sodium = +servingCount * +preCalculatedValues[servingSize]?.sodium;
      sodium = +sodium.toFixed(3);
      setSodium(() => sodium);
    }
  }, [sizeQuantityMap, servingCount, servingSize, defaultValues]);

  const fetchServingSizeOptions = async (editDish: any) => {
    if (editDish) {
      let options = [];
      if (editDish?.unitName === "Plate") {
        let preCalculated = editDish?.recipe?.preCalculated;
        if (typeof preCalculated?.Small !== "undefined") {
          let obj = {
            display_unit: "Small plate",
            display_unit_plural: "Small plates",
            id: "",
            name: "Plate",
            serving_qty: preCalculated?.Small?.standard_values_in_grams,
            serving_unit: "grams",
            size: "Small",
          };
          options.push(obj);

          obj = {
            display_unit: "Medium plate",
            display_unit_plural: "Medium plates",
            id: "",
            name: "Plate",
            serving_qty: preCalculated?.Medium?.standard_values_in_grams,
            serving_unit: "grams",
            size: "Medium",
          };
          options.push(obj);

          obj = {
            display_unit: "Large plate",
            display_unit_plural: "Large plates",
            id: "",
            name: "Plate",
            serving_qty: preCalculated?.Large?.standard_values_in_grams,
            serving_unit: "grams",
            size: "Large",
          };
          options.push(obj);
        }
        setServingSize(() => "Small");
      } else {
        if (!!editDish?.unitName) {
          try {
            const res = await makeRequest(
              nutritionApiService.getUnitOptions(editDish?.unitName)
            );
            options = res?.data?.data;
          } catch (error: any) {
            toast.error("Error fetching unit options!");
          }
        }
      }
      setServiceSizeOptions(options);
    }
  };

  const onClose = () => {
    setOpen(false);
  };
  const clear = () => {
    setServingCount(0);
    setServingSize("");
    setCalorie(0);
    setProtein(0);
    setCarbs(0);
    setFat(0);
    setFattyAcidTotalSaturated(0);
    setFibre(0);
    setSodium(0);
    setTotalQuantity(0);
    setDefaultValues({
      ...defaultValues,
      calorie: 0,
      protein: 0,
      carbs: 0,
      fat: 0,
      fattyAcidTotalSaturated: 0,
      fibre: 0,
      sodium: 0,
      servingQuantity: 0,
    });
  };

  const handleSubmit = (e: any) => {
    e.preventDefault();

    const newMealPlan = mealPlan.map((item: any) => {
      if (item?.date === date) {
        item?.meals.forEach((x: any) => {
          if (x.mealType === mealType) {
            let obj = x.dishes[editId];
            obj = {
              ...obj,
              unitCount: servingCount,
              protein: protein,
              calorie: calorie,
              totalQuantity: totalQuantity,
              unitSize: servingSize,
              carbs: carbs,
              fat: fat,
              fattyAcidTotalSaturated: fattyAcidTotalSaturated,
              fibre: fibre,
              sodium: sodium,
            };
            x.dishes[editId] = obj;
          }
        });
      }
      return item;
    });
    updateMealPlan(newMealPlan);
    clear();
    setOpen(false);
  };

  return (
    <Modal
      show={open}
      //   centered={windowView === SCREEN_TYPE.MD_VIEW}
      className="desktop-modal meal-modal"
    >
      <Modal.Header>
        <Modal.Title className="heading">
          Edit Dish of {mealType} on {date}{" "}
        </Modal.Title>
        <div onClick={onClose}>
          <img src={closeIcon} alt="close-icon" />
        </div>
      </Modal.Header>

      <Modal.Body>
        <div className="add-meal">
          <form action="" onSubmit={handleSubmit}>
            <div className="form-group mb-3">
              <label htmlFor="" className="mb-2">
                Dish Name
              </label>
              <input
                type="text"
                className="form-control"
                disabled
                defaultValue={editDish?.name}
              />
            </div>
            <div className="form-group mb-3">
              <label htmlFor="" className="mb-0">
                Serving Unit
              </label>
              <input
                type="text"
                disabled
                className="form-control"
                defaultValue={editDish?.unitName}
              />
            </div>
            <div className="form-group mb-3">
              <label htmlFor="" className="mb-2">
                Serving Count
              </label>
              {editDish?.unitName === "Scoop" ? (
                <input
                  type="number"
                  className="form-control"
                  value={servingCount}
                  onChange={(e) => setServingCount(+e.target.value)}
                  min={0.5}
                  max={10}
                  step={0.5}
                />
              ) : (
                <input
                  type="number"
                  className="form-control"
                  value={servingCount}
                  onChange={(e) => {
                    let newCount = Math.abs(parseInt(e.target.value));
                    setServingCount(newCount);
                  }}
                  min={1}
                  max={10}
                />
              )}
            </div>
            {/* {editDish?.unitSize && ( */}
            <div className="form-group mb-3">
              <label htmlFor="" className="mb-2">
                Serving Size
              </label>
              <select
                name=""
                id=""
                className="form-select"
                onChange={(e) => {
                  setServingSize(e.target.value);
                }}
              >
                {servingSizeOptions?.map((item: any, index: any) => {
                  return (
                    <option
                      selected={item.size === servingSize}
                      value={item?.size}
                      key={index + 1}
                    >
                      {item?.size}
                    </option>
                  );
                })}
              </select>
            </div>
            {/* )} */}
            <button className="btn btn-outline-secondary">Edit Dish</button>
          </form>
        </div>
        {
          <div className="mt-4">
            <p className="mb-2">
              <span>
                Serving Quantity : {(totalQuantity / servingCount).toFixed(3)}{" "}
                {editDish?.servingUnit}
              </span>
            </p>
            <p className="mb-2">
              <span>
                Total Quantity : {totalQuantity.toFixed(3)}{" "}
                {editDish?.servingUnit}
              </span>
            </p>
            <p className="mb-2">
              <span>Total Protein : {protein}</span>
            </p>
            <p className="mb-2">
              <span>Total Calorie : {calorie}</span>
            </p>
          </div>
        }
      </Modal.Body>
    </Modal>
  );
};

export default EditDishModal;
